import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import { flowColumns } from '../columns';
import { useSessionFlows } from '../hooks';

const PAGE_SIZE = 50;

/** A session's connections, newest first, a page at a time. */
export function SessionFlows({ sessionId }: { sessionId: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useSessionFlows(sessionId, page, PAGE_SIZE);
  const total = data?.total ?? 0;
  const last = Math.min(page * PAGE_SIZE, total);
  return (
    <div className="space-y-2">
      <ResponsiveTable
        columns={flowColumns}
        rows={data?.flows ?? []}
        rowKey={(f) => f.flowId}
        isLoading={isLoading}
        empty={
          isError ? (
            <EmptyState variant="error" size="inline" title="Couldn't load the connections" />
          ) : (
            <EmptyState
              size="inline"
              title="No connections recorded"
              description="A connection's record arrives a few minutes after it ends; a long one also gets one every five minutes while it's open."
            />
          )
        }
      />
      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {(page - 1) * PAGE_SIZE + 1}–{last} of {total}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => { setPage((p) => p - 1); }}>
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled={last >= total} onClick={() => { setPage((p) => p + 1); }}>
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
